/**
 * Catálogo de los BLOQUES que puede llevar el reporte completo (§2.4, §16).
 *
 * Es el equivalente de `lib/vista.ts` para el módulo de Reportes: el usuario
 * marca los bloques en una lista y el libro sale con una hoja por bloque, en el
 * orden de este arreglo. Vive en `shared` —y no en la PWA— porque el Worker
 * VALIDA contra él: un id que no esté aquí se descarta en vez de generar una
 * hoja vacía, y así la lista de la pantalla y la que sabe generar el API no
 * pueden separarse.
 *
 * `periodo` dice de qué parámetro depende cada bloque, que es lo que decide qué
 * controles se habilitan:
 *   - `anio`  → un año completo (el dato nace en grano de mes).
 *   - `rango` → el [desde, hasta] del filtro (el único con grano de día).
 */

export const REPORTE_CATALOG = [
  {
    id: 'totalizacion',
    label: 'Totalización mensual',
    hoja: 'Totalización',
    periodo: 'anio',
    detalle:
      'Todos los KPIs del rollup por mes y sede: Diamond, ocupadas s/CH, House, Comp, total de noches, disponibles, % ocupación, ADR, RevPAR, Room Revenue e ingreso total (Bs y $).',
  },
  {
    id: 'noches',
    label: 'Libro de Noches',
    hoja: 'Noches',
    periodo: 'anio',
    detalle:
      'Diamond / Comp / House / Total por mes y sede, con los meses incompletos marcados con sus días cargados.',
  },
  {
    id: 'cuentas',
    label: 'Cuentas corporativas',
    hoja: 'Cuentas',
    periodo: 'anio',
    detalle: 'Noches por cuenta, sede y mes del libro «RESULTADOS ACCOUNTS CDR», con su ranking.',
  },
  {
    id: 'canales',
    label: 'Canales de venta',
    hoja: 'Canales',
    periodo: 'anio',
    detalle:
      'Noches por canal (CDR / VIP / Ventas / Front Desk / Expedia) con su mix, el detalle por mes y la matriz canal × sede.',
  },
  {
    id: 'expedia',
    label: 'Expedia',
    hoja: 'Expedia',
    periodo: 'anio',
    detalle:
      'Noches, reservas, cancelaciones e importe de Expedia, con su desglose por tipo de habitación.',
  },
  {
    id: 'habitaciones',
    label: 'Tipos de habitación',
    hoja: 'Habitaciones',
    periodo: 'rango',
    detalle:
      'Ocupación por tipo de habitación en el rango, con el % de cada tipo contra SU inventario y la cobertura sobre el hotel.',
  },
  {
    id: 'markets',
    label: 'Rate codes (Res. Statistics 2)',
    hoja: 'Rate codes',
    periodo: 'rango',
    detalle:
      'Qué produjo cada rate code del Reservation Statistics 2, cuánto pesa Diamond dentro del total y los días que no cuadran con su Grand Total.',
  },
  {
    id: 'operacion',
    label: 'Movimiento y pace',
    hoja: 'Movimiento',
    periodo: 'rango',
    detalle:
      'Walk-in, day use, no-show, salidas anticipadas, reservas hechas y canceladas, tarifa media por persona y la ocupación ya vendida a 7/31/365 días.',
  },
] as const;

export type ReporteId = (typeof REPORTE_CATALOG)[number]['id'];

export const REPORTE_IDS: ReporteId[] = REPORTE_CATALOG.map((r) => r.id);

export function isReporteId(x: string): x is ReporteId {
  return (REPORTE_IDS as string[]).includes(x);
}

/** Normaliza una selección al orden del catálogo y sin repetidos. */
export function sortReportes(ids: readonly ReporteId[]): ReporteId[] {
  const puestos = new Set<ReporteId>(ids);
  return REPORTE_IDS.filter((id) => puestos.has(id));
}

/**
 * Lee el `bloques=` de la URL. Ausente = TODOS (el caso normal no ensucia la
 * query); presente y vacío = ninguno, que es un 400 en el API porque un libro
 * sin hojas no es un libro.
 */
export function parseBloques(raw: string | null | undefined): ReporteId[] {
  if (raw == null) return [...REPORTE_IDS];
  return sortReportes(raw.split(',').filter(isReporteId));
}
